import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Anchor, Box, Button, Flex, Group, Modal, Textarea, Tooltip } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { useForm } from "@mantine/form";

import { HolidayResponse } from "./AbsencesTab";

import { ChevronSelect } from "pages/todo/list/new/DateTimeSelect";
import ConfirmationModal from "components/modal/confirmation";
import SuccessModal from "components/modal/success";

import { IRootState } from "config/redux";
import { AppConstants, ServiceConstants } from "constant";
import { useServiceSuccess } from "hooks";
import { AbsenceService, UserService } from "services";
import { DateUtils, StorageUtils } from "utils";
import { UserResponse } from "types";

const { STORAGE_USER } = AppConstants;
const { 
  REQUEST_ABSENCE_CREATE_ID, 
  REQUEST_ABSENCE_VIEW_ID,
  REQUEST_ABSENCE_UPDATE_ID,
  REQUEST_ABSENCE_DELETE_ID,
  REQUEST_ABSENCE_CANCEL_ID,
  REQUEST_USERS_LIST_ID, 
  REQUEST_PENDING 
} = ServiceConstants;

interface AbsenceFormValues {
  dateRange: [Date | null, Date | null];
  reason: string | null;
  replacementAgentId: string | null;
  note: string;
}

const REASON_OPTIONS = [
  { value: "VACATION", label: "Odmor" },
  { value: "FREE_DAYS", label: "Slobodni dani" },
  { value: "SICK_LEAVE", label: "Bolovanje" },
  { value: "CHILD_BIRTH", label: "Rođenje deteta" },
];

interface AbsenceRequestModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  absenceId?: number | null;
  holidays?: HolidayResponse[];
}

const AbsenceRequestModal = ({ opened, onClose, onSuccess, absenceId, holidays = [] }: AbsenceRequestModalProps) => {
  const [agents, setAgents] = useState<{ value: string; label: string }[]>([]);
  const [absenceStatus, setAbsenceStatus] = useState<string | null>(null);
  const [confirmModalOpened, setConfirmModalOpened] = useState(false);
  const [successModalOpened, setSuccessModalOpened] = useState(false);

  const [loggedUser] = useState<UserResponse | undefined>(StorageUtils.getObject(STORAGE_USER));

  const isEditMode = !!absenceId;

  // Create a map for quick holiday lookup
  const holidayMap = useMemo(() => {
    const map = new Map<string, string>();
    holidays.forEach((h) => {
      map.set(h.date, h.name);
    });
    return map;
  }, [holidays]);

  // Form state using Mantine useForm
  const form = useForm<AbsenceFormValues>({
    mode: 'controlled',
    initialValues: {
      dateRange: [null, null],
      reason: null,
      replacementAgentId: null,
      note: '',
    },
  });

  // Reset form helper
  const resetForm = useCallback(() => {
    form.reset();
    setAbsenceStatus(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Success handler for create/update
  const handleSuccess = useCallback(() => {
    resetForm();
    onClose();
    onSuccess?.();
  }, [onClose, onSuccess, resetForm]);

  // Success handler for delete/cancel
  const handleDeleteSuccess = useCallback(() => {
    setConfirmModalOpened(false);
    setSuccessModalOpened(true);
  }, []);

  // Service success hooks - replace manual useRef/useEffect patterns
  const { isLoading: isCreateLoading } = useServiceSuccess(REQUEST_ABSENCE_CREATE_ID, { 
    onSuccess: handleSuccess 
  });
  const { isLoading: isUpdateLoading } = useServiceSuccess(
    absenceId ? REQUEST_ABSENCE_UPDATE_ID + absenceId : undefined, 
    { onSuccess: handleSuccess }
  );
  const { isLoading: isDeleteLoading } = useServiceSuccess(
    absenceId ? REQUEST_ABSENCE_DELETE_ID + absenceId : undefined, 
    { onSuccess: handleDeleteSuccess }
  );
  const { isLoading: isCancelLoading } = useServiceSuccess(
    absenceId ? REQUEST_ABSENCE_CANCEL_ID + absenceId : undefined, 
    { onSuccess: handleDeleteSuccess }
  );

  const viewServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_ABSENCE_VIEW_ID]);
  const usersServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_USERS_LIST_ID]);

  const isLoading = isCreateLoading || isUpdateLoading || isDeleteLoading || isCancelLoading;

  // Reset form when opening in create mode (no absenceId)
  useEffect(() => {
    if (opened && !absenceId) {
      resetForm();
    }
  }, [opened, absenceId, resetForm]);

  // Fetch agents list when modal opens
  useEffect(() => {
    if (opened) {
      UserService.usersAll({ page: 0, search: '', profileId: 2, status: 'ACTIVE', type: 'EMPLOYEE' });
    }
  }, [opened]);

  // Fetch absence data when editing
  useEffect(() => {
    if (opened && absenceId) {
      AbsenceService.absenceOne(absenceId);
    }
  }, [opened, absenceId]);

  // Populate form when editing
  useEffect(() => {
    if (viewServiceObject?.status === 'SUCCESS' && viewServiceObject?.data && isEditMode) {
      const absence = viewServiceObject.data;
      form.setValues({
        dateRange: [new Date(absence.dateFrom), new Date(absence.dateTo)],
        reason: absence.reason || null,
        replacementAgentId: absence.replacementAgent?.id ? String(absence.replacementAgent.id) : null,
        note: absence.note || "",
      });
      setAbsenceStatus(absence.status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewServiceObject, isEditMode]);

  // Process agents list
  useEffect(() => {
    if (usersServiceObject?.status === 'SUCCESS' && usersServiceObject?.data?.content) {
      const agentOptions = usersServiceObject.data.content
        .filter((agent: UserResponse) => agent.id !== loggedUser?.id) // Exclude self
        .map((agent: UserResponse) => ({
          value: String(agent.id),
          label: agent.fullName,
        }));
      setAgents(agentOptions);
    }
  }, [usersServiceObject, loggedUser?.id]);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = useCallback(() => {
    const { dateRange, reason, replacementAgentId, note } = form.values;
    if (!dateRange[0] || !dateRange[1] || !loggedUser?.id) return;

    const data = {
      agentId: loggedUser.id,
      dateFrom: formatDate(dateRange[0]),
      dateTo: formatDate(dateRange[1]),
      reason: reason || undefined,
      replacementAgentId: replacementAgentId ? Number(replacementAgentId) : undefined,
      note: note || undefined,
    };

    if (isEditMode && absenceId) {
      AbsenceService.absenceUpdate(absenceId, data);
    } else {
      AbsenceService.absenceCreate(data);
    }
  }, [form.values, loggedUser?.id, isEditMode, absenceId]);

  const handleOpenConfirmModal = useCallback(() => {
    setConfirmModalOpened(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!absenceId) return;
    
    if (absenceStatus === 'PENDING') {
      AbsenceService.absenceDelete(absenceId);
    } else if (absenceStatus === 'APPROVED') {
      AbsenceService.absenceCancel(absenceId);
    }
  }, [absenceId, absenceStatus]);

  const handleSuccessModalClose = useCallback(() => {
    setSuccessModalOpened(false);
    resetForm();
    onClose();
    onSuccess?.();
  }, [onClose, onSuccess, resetForm]);

  const canSubmit = useMemo(() => {
    const { dateRange, replacementAgentId } = form.values;
    return dateRange[0] && dateRange[1] && replacementAgentId && !isLoading;
  }, [form.values, isLoading]);

  const canCancelRequest = useMemo(() => {
    return isEditMode && (absenceStatus === 'PENDING' || absenceStatus === 'APPROVED');
  }, [isEditMode, absenceStatus]);

  return (
    <>
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEditMode ? "Zahtev za odsustvo" : "Novi zahtev za odsustvo"}
      size="lg"
      centered
      padding="xl"
      styles={{ title: { fontSize: 24, fontWeight: 600 } }}
    >
      <Flex gap="xl" p="md">
        {/* Kalendar za izbor datuma */}
        <Box p="md" style={{ 
            border: '1px solid var(--mantine-color-gray-3)', 
            borderRadius: 8,
            backgroundColor: isEditMode ? 'var(--mantine-color-gray-1)' : undefined,
          }}>
          <DatePicker
            type="range"
            allowSingleDateInRange
            value={form.values.dateRange}
            onChange={(value) => form.setFieldValue('dateRange', value)}
            color="primary"
            getDayProps={(date) => {
              const today = new Date();
              const isToday = date.toDateString() === today.toDateString();
              const { dateRange } = form.values;
              const isInRange = dateRange[0] && dateRange[1] && 
                date >= dateRange[0] && date <= dateRange[1];
              const dateString = DateUtils.formatDateLocal(date);
              const isHoliday = holidayMap.has(dateString);
              
              return {
                style: {
                  // Holiday always takes precedence (purple)
                  ...(isHoliday && {
                    backgroundColor: 'var(--mantine-color-violet-6)',
                    color: 'white',
                    borderRadius: 4,
                  }),
                  // Selected range but not holiday (green)
                  ...(isInRange && !isHoliday && {
                    backgroundColor: 'var(--mantine-color-primary-6)',
                    color: 'white',
                    borderRadius: 4,
                  }),
                  // Today marker (only if not selected and not holiday)
                  ...(isToday && !isInRange && !isHoliday && {
                    border: '2px solid var(--mantine-color-gray-5)',
                  }),
                },
              };
            }}
            renderDay={(date) => {
              const dateString = DateUtils.formatDateLocal(date);
              const holidayName = holidayMap.get(dateString);
              
              if (holidayName) {
                return (
                  <Tooltip 
                    label={holidayName} 
                    withArrow
                    color="white"
                    styles={{
                      tooltip: {
                        color: 'var(--mantine-color-violet-6)',
                        fontWeight: 500,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    <div>{date.getDate()}</div>
                  </Tooltip>
                );
              }
              return date.getDate();
            }}
          />
        </Box>

        {/* Forma */}
        <Box style={{ flex: 1 }}>
          <ChevronSelect
            label="Razlog odsustva"
            placeholder="Izaberite razlog"
            data={REASON_OPTIONS}
            value={form.values.reason}
            onChange={(value) => form.setFieldValue('reason', value)}
            mb="md"
            styles={isEditMode ? { input: { backgroundColor: 'var(--mantine-color-gray-1)' } } : undefined}
          />
          <ChevronSelect
            label="Zamena"
            placeholder="Izaberite agenta"
            data={agents}
            value={form.values.replacementAgentId}
            onChange={(value) => form.setFieldValue('replacementAgentId', value)}
            mb="md"
            withAsterisk
            styles={isEditMode ? { input: { backgroundColor: 'var(--mantine-color-gray-1)' } } : undefined}
          />
          <Textarea
            label="Napomena"
            value={form.values.note}
            onChange={(e) => form.setFieldValue('note', e.currentTarget.value)}
            minRows={4}
            styles={isEditMode ? { input: { backgroundColor: 'var(--mantine-color-gray-1)' } } : undefined}
          />
        </Box>
      </Flex>

      {canCancelRequest && (
        <Anchor c="red" size="sm" ml="md" onClick={handleOpenConfirmModal} style={{ cursor: 'pointer' }}>
          Otkaži zahtev za odsustvo
        </Anchor>
      )}

      <Group justify="flex-end" mt="md">
        <Button variant="outline" color="gray" radius="md" onClick={onClose}>
          Otkaži
        </Button>
        <Button color="primary" radius="md" onClick={handleSubmit} loading={isLoading} disabled={!canSubmit}>
          Sačuvaj
        </Button>
      </Group>
    </Modal>

    <ConfirmationModal
      opened={confirmModalOpened}
      onClose={() => setConfirmModalOpened(false)}
      onConfirm={handleConfirmDelete}
      title="Brisanje zahteva za odsustvo"
      message="Da li ste sigurni da želite da obrišete zahtev za odsustvo?"
      confirmLabel="Obriši zahtev"
      loading={isLoading}
    />

    <SuccessModal
      opened={successModalOpened}
      onClose={handleSuccessModalClose}
      message="Uspešno brisanje zahteva za odsustvo!"
    />
  </>
  );
};

export default AbsenceRequestModal;
